import {useEffect, useMemo, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {KTIcon} from '../../../../helpers'
import Swal from 'sweetalert2'
import {
  buildPermissionRowsFromCatalog,
  createAdminRole,
  getAdminPermissionsCatalog,
  slugifyRoleSlug,
  type PermissionRowFromCatalog,
} from '../../../../../app/modules/auth/core/_requests'

type PermRow = PermissionRowFromCatalog

type ModalAddRoleProps = {
  show: boolean
  setShow: (v: boolean) => void
  onSuccess?: () => void
}

const ModalAddRole = ({show, setShow, onSuccess}: ModalAddRoleProps) => {
  const [roleName, setRoleName] = useState('')
  const [roleDescription, setRoleDescription] = useState('')
  const [permissions, setPermissions] = useState<PermRow[]>([])
  const [errorMsg, setErrorMsg] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const resetForm = () => {
    setRoleName('')
    setRoleDescription('')
    setErrorMsg('')
    setPermissions((prev) =>
      prev.map((ele) => ({...ele, can_read: false, can_write: false, can_edit: false}))
    )
  }

  const getData = async () => {
    try {
      const powers = await getAdminPermissionsCatalog()
      setPermissions(buildPermissionRowsFromCatalog(powers))
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    if (show) {
      getData()
      setRoleName('')
      setRoleDescription('')
      setErrorMsg('')
    }
  }, [show])

  const anyPermissionSelected = useMemo(
    () => permissions.some((p) => p.can_read || p.can_write || p.can_edit),
    [permissions]
  )

  const allSelected = useMemo(
    () =>
      permissions.length > 0 && permissions.every((p) => p.can_read && p.can_write && p.can_edit),
    [permissions]
  )

  const blogPermissions = permissions.filter((p) => p.label?.toLowerCase().includes('blog') || p.name?.toLowerCase().includes('blog'))
  const otherPermissions = permissions.filter((p) => !p.label?.toLowerCase().includes('blog') && !p.name?.toLowerCase().includes('blog'))

  const allBlogSelected = useMemo(
    () => blogPermissions.length > 0 && blogPermissions.every((p) => p.can_read && p.can_write && p.can_edit),
    [blogPermissions]
  )

  const toggleBlogPermissions = (checked: boolean) => {
    setPermissions((prev) =>
      prev.map((p) =>
        (p.label?.toLowerCase().includes('blog') || p.name?.toLowerCase().includes('blog'))
          ? {...p, can_read: checked, can_write: checked, can_edit: checked}
          : p
      )
    )
  }

  const togglePermission = (perm: PermRow, checked: boolean) => {
    setPermissions((prev) =>
      prev.map((p) =>
        p.uuid === perm.uuid ? {...p, can_read: checked, can_write: checked, can_edit: checked} : p
      )
    )
  }

  const selectAll = (checked: boolean) => {
    setPermissions((prev) =>
      prev.map((item) => ({
        ...item,
        can_read: checked,
        can_write: checked,
        can_edit: checked,
      }))
    )
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMsg('')
    if (!roleName.trim() || !roleDescription.trim()) {
      setErrorMsg('Name and description are required.')
      return
    }
    
    const nameRegex = /^[A-Za-z\s]+$/
    if (!nameRegex.test(roleName.trim())) {
      Swal.fire({
        titleText: 'Error!',
        text: 'Role Name should accept only alphabetical characters.',
        icon: 'error',
        confirmButtonText: 'Ok! Got It',
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-primary px-6 rounded-3',
        },
      })
      return
    }

    if (!anyPermissionSelected) {
      setErrorMsg('* Please select at least one permission *')
      return
    }

    const permission_codes = permissions
      .filter((ele) => ele.can_read || ele.can_write || ele.can_edit)
      .map((p) => p.uuid)

    setSubmitting(true)
    try {
      await createAdminRole({
        slug: slugifyRoleSlug(roleName),
        display_name: roleName.trim(),
        description: roleDescription.trim(),
        permission_codes,
      })
      await Swal.fire({
        title: 'Success',
        text: 'Role created successfully.',
        icon: 'success',
        confirmButtonText: 'OK',
      })
      resetForm()
      setShow(false)
      onSuccess?.()
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        err?.message ||
        'Something went wrong.'
      Swal.fire('Error', typeof msg === 'string' ? msg : 'Something went wrong.', 'error')
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    resetForm()
    setShow(false)
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size='lg'
      className='modal-create-role'
      contentClassName='rounded-4 border-0 shadow-lg overflow-hidden'
    >
      <div className='modal-content border-0'>
        <div className='modal-header border-0 pb-2 pt-8 px-8 bg-white'>
          <h2 className='fw-bold fs-3 mb-0' style={{color: '#1e3a5f'}}>
            Create Role
          </h2>
          <button
            type='button'
            className='btn btn-icon btn-sm btn-light rounded-circle'
            onClick={handleClose}
            aria-label='Close'
          >
            <KTIcon iconName='cross' className='fs-2 text-gray-600' />
          </button>
        </div>

        <form onSubmit={onSubmit}>
          <div className='modal-body px-8 pt-0 pb-2'>
            {errorMsg && !errorMsg.startsWith('*') && (
              <div className='alert alert-danger py-3 px-4 mb-6 rounded-3 border-0 text-center'>
                {errorMsg}
              </div>
            )}

            <div className='mb-8'>
              <h3 className='fw-bold fs-5 mb-5' style={{color: '#1e3a5f'}}>
                Role Details
              </h3>
              <div className='mb-5'>
                <label className='form-label fw-semibold text-gray-800 text-uppercase fs-9'>
                  Role Name <span className='text-danger'>*</span>
                </label>
                <input
                  className='form-control rounded-3 border border-primary border-opacity-25 bg-white py-3 fw-bold'
                  style={{borderColor: '#b8d4f0'}}
                  placeholder='Enter role name'
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                  autoComplete='off'
                />
              </div>
              <div>
                <label className='form-label fw-semibold text-gray-800 text-uppercase fs-9'>
                  Role Description <span className='text-danger'>*</span>
                </label>
                <input
                  className='form-control rounded-3 border border-primary border-opacity-25 bg-white py-3 fw-bold'
                  style={{borderColor: '#b8d4f0'}}
                  placeholder='Enter role description'
                  value={roleDescription}
                  onChange={(e) => setRoleDescription(e.target.value)}
                  autoComplete='off'
                />
              </div>
            </div>

            <div>
              <h3 className='fw-bold fs-5 mb-4' style={{color: '#1e3a5f'}}>
                Role Permissions
              </h3>

              <div className='d-flex align-items-center gap-2 mb-4 pb-3 border-bottom border-gray-200'>
                <label className='form-check form-check-custom form-check-solid form-check-success mb-0 cursor-pointer'>
                  <input
                    className='form-check-input border-success'
                    type='checkbox'
                    checked={allSelected}
                    onChange={(e) => selectAll(e.target.checked)}
                  />
                  <span className='form-check-label fw-semibold text-gray-800'>Select All</span>
                </label>
                <OverlayInfo text='Select all permissions for this role' />
              </div>

              <div className='d-flex flex-column gap-2'>
                {otherPermissions.map((permission) => {
                  const checked = permission.can_read || permission.can_write || permission.can_edit
                  return (
                    <div
                      key={permission.uuid}
                      className='d-flex align-items-center gap-2 py-2 px-3 rounded-3 bg-white border border-gray-100'
                    >
                      <label className='form-check form-check-custom form-check-solid mb-0 flex-grow-1 cursor-pointer'>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          checked={checked}
                          onChange={(e) => togglePermission(permission, e.target.checked)}
                        />
                        <span className='form-check-label text-gray-800 fw-semibold fs-6'>
                          {permission.label}
                        </span>
                      </label>
                      <OverlayInfo text={permission.name} />
                    </div>
                  )
                })}
              </div>

              {blogPermissions.length > 0 && (
                <div className='mt-6 p-4 rounded-3' style={{backgroundColor: '#f8fafc', border: '1px solid #e2e8f0'}}>
                  <div className='d-flex align-items-center gap-2 mb-4 pb-3 border-bottom border-gray-200'>
                    <label className='form-check form-check-custom form-check-solid form-check-primary mb-0 cursor-pointer'>
                      <input
                        className='form-check-input border-primary'
                        type='checkbox'
                        checked={allBlogSelected}
                        onChange={(e) => toggleBlogPermissions(e.target.checked)}
                      />
                      <span className='form-check-label fw-bold text-gray-900 fs-5'>Blog Access</span>
                    </label>
                    <OverlayInfo text='Select all blog-related permissions' />
                  </div>
                  
                  <div className='d-flex flex-column gap-2 ps-6'>
                    {blogPermissions.map((permission) => {
                      const checked = permission.can_read || permission.can_write || permission.can_edit
                      return (
                        <div
                          key={permission.uuid}
                          className='d-flex align-items-center gap-2 py-2 px-3 rounded-3 bg-white border border-gray-100'
                        >
                          <label className='form-check form-check-custom form-check-solid mb-0 flex-grow-1 cursor-pointer'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              checked={checked}
                              onChange={(e) => togglePermission(permission, e.target.checked)}
                            />
                            <span className='form-check-label text-gray-800 fw-semibold fs-6'>
                              {permission.label}
                            </span>
                          </label>
                          <OverlayInfo text={permission.name} />
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {!anyPermissionSelected && (
                <p className='text-danger fs-7 mt-4 mb-0 fw-semibold text-center'>
                  * Please select at least one permission *
                </p>
              )}
            </div>
          </div>

          <div className='modal-footer border-0 bg-white pt-4 px-8 pb-8 justify-content-end gap-3'>
            <button
              type='button'
              className='btn btn-light border border-primary border-opacity-50 text-gray-800 px-6 rounded-3 fw-bold'
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type='submit'
              className='btn btn-primary px-10 rounded-3 fw-bold shadow-sm'
              disabled={submitting}
            >
              {submitting ? 'Please wait...' : 'Create Role'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  )
}

function OverlayInfo({text}: {text: string}) {
  return (
    <span className='text-gray-400' title={text}>
      <i className='fas fa-info-circle fs-7' />
    </span>
  )
}

export default ModalAddRole
